import React, { useState, useEffect } from "react";
import { Button, Notification, Spinner, Text } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import {
    FORM_ID,
    IdentificationMethod as IdentificationMethodType,
    VERIFICATION_TYPE,
} from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { ProgressBar } from "../../../../components";
import { FieldSet, Input } from "../../../../forms";
import { RapidForm, actionTypes as RF_AT } from "../../../../rapidform";
import { cancelBankIdPolling, getRetailCustomerBankIdResult, setRetailCustomer } from "../../../state/actions";
import { clearAll } from "../../../../rapidform/state/actions";
import { getErrorMessage } from "../../../../rapidform/state/actions.util";
import { allValidationRules } from "../../../../rapidform/formValidation";
import {
    getBankIdResult,
    getExistingPhoneNumberLookup,
    hasVerifiedIdentification,
    shouldPollBankId as shouldPollBankIdSelector,
} from "../../../state/selectors/customerSelectors";
import { getFormById } from "../../../state/selectors/formSelectors";
import TEXT from "../../../text-consts";
import { IdentificationMethod } from "../../atoms/IdentificationMethod";
import "./Identification.less";
import ContactCard from "../ContactCard/ContactCard";
import { CustomerSection } from "../CustomerSection";

const IDENTIFICATION_VALIDATION_NAMES: Partial<Record<IdentificationMethodType, string>> = {
    PASSPORT: "passportNumber",
    NATIONAL_ID_CARD: "nationalId",
    DRIVERS_LICENSE: "driversLicense",
    BANK_ID: "bankId",
};

const POLL_INTERVAL = 15000;
const MAX_POLL_DURATION = 10 * 60 * 1000;

const isEmailAddress = (value: string): boolean => /\S+@\S+\.\S+/.test(value);

const Identification = () => {
    const dispatch = useDispatch();
    const form = useSelector((state: RootState) => getFormById(state, FORM_ID.CUSTOMER_IDENTIFICATION));
    const bankIdResult = useSelector((state: RootState) => getBankIdResult(state));
    const existingPhoneNumberLookup = useSelector((state: RootState) => getExistingPhoneNumberLookup(state));
    const hasVerified = useSelector((state: RootState) => hasVerifiedIdentification(state));
    const shouldPollBankId = useSelector((state: RootState) => shouldPollBankIdSelector(state));
    const selectedIdentificationMethod = form.values.selectedIdentificationMethod as
        | IdentificationMethodType
        | undefined;
    const bankIdContactValue = (form?.values?.identity?.bankId as string) || "";
    const [sentBankIdValue, setSentBankIdValue] = useState("");
    const bankIdWaitingChannel = isEmailAddress(sentBankIdValue || bankIdContactValue) ? "e-post" : "SMS";
    const [error, setError] = useState<string | null>(null);
    const [showIdentificationForm, setShowIdentificationForm] = useState(!hasVerified);
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

    const resetIdentification = () => {
        setSentBankIdValue("");
        dispatch(clearAll(FORM_ID.CUSTOMER_IDENTIFICATION));
        setShowIdentificationForm(true);
    };

    useEffect(() => {
        if (hasVerified) {
            setShowIdentificationForm(false);
        }
    }, [hasVerified]);

    useEffect(() => {
        if (
            selectedIdentificationMethod !== VERIFICATION_TYPE.BANK_ID ||
            !existingPhoneNumberLookup ||
            bankIdContactValue.trim().length > 0
        ) {
            return;
        }

        dispatch({
            type: RF_AT.FORM_VALUE_CHANGED(FORM_ID.CUSTOMER_IDENTIFICATION),
            payload: {
                errorMessage: undefined,
                form: FORM_ID.CUSTOMER_IDENTIFICATION,
                inputName: "identity-bankId",
                inputValue: existingPhoneNumberLookup,
                visible: true,
            },
        });
    }, [selectedIdentificationMethod, existingPhoneNumberLookup, bankIdContactValue, dispatch]);

    const isBankIdAlreadySent = bankIdContactValue.trim() === sentBankIdValue;

    const handleSubmit = async () => {
        if (selectedIdentificationMethod === VERIFICATION_TYPE.BANK_ID && isBankIdAlreadySent) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await dispatch(setRetailCustomer());

            if (response.success) {
                if (selectedIdentificationMethod === VERIFICATION_TYPE.BANK_ID) {
                    setSentBankIdValue(bankIdContactValue.trim());
                }
                setShowIdentificationForm(false);
            }
        } catch (e) {
            setError(
                typeof e === "object" &&
                    e !== null &&
                    "displayableMessage" in e &&
                    typeof e.displayableMessage === "string"
                    ? e.displayableMessage
                    : TEXT.ERRORS.RETAILER_LOOKUP_ERROR,
            );
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!shouldPollBankId) return;

        const pollInterval = setInterval(() => {
            dispatch(getRetailCustomerBankIdResult());
        }, POLL_INTERVAL);

        const pollTimeout = setTimeout(() => {
            dispatch(cancelBankIdPolling());
            setSentBankIdValue("");
            setError("Vi har ikke mottatt BankID-verifisering fra kunden. Du kan sende lenken på nytt.");
        }, MAX_POLL_DURATION);

        return () => {
            clearInterval(pollInterval);
            clearTimeout(pollTimeout);
        };
    }, [shouldPollBankId]);

    const handleUpdateStatus = async () => {
        setIsUpdatingStatus(true);
        try {
            await dispatch(getRetailCustomerBankIdResult());
        } catch (e) {
            setError(TEXT.ERRORS.RETAILER_LOOKUP_ERROR);
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    return (
        <CustomerSection className="identification" heading="Legitimering" error={error}>
            {hasVerified && !showIdentificationForm ? (
                <>
                    <ContactCard
                        name={`${bankIdResult?.firstName || ""} ${bankIdResult?.lastName || ""}`.trim()}
                        address={bankIdResult?.birthdate || ""}
                        showTriggerButton={!showIdentificationForm}
                        triggerFunction={() => resetIdentification()}
                        triggerText="Ny Identifisering"
                    />
                    <Notification type="tile" variant="success" mt="200" className="identification__notification">
                        <Notification.Icon name="check-circle" />
                        <Notification.Text>
                            {verificationSuccessMessage(form.values.selectedIdentificationMethod)}
                        </Notification.Text>
                    </Notification>
                </>
            ) : (
                <Notification type="tile" variant="information" className="identification__notification">
                    <Notification.Icon />
                    <Notification.Text>
                        Man er pålagt å identifisere kundene, og dokumentere at det er en gyldig legitimasjon med riktig
                        kontrollnummer.
                    </Notification.Text>
                </Notification>
            )}
            {(showIdentificationForm || !hasVerified) && (
                <RapidForm
                    id={FORM_ID.CUSTOMER_IDENTIFICATION}
                    action={"#"}
                    values={form?.values || {}}
                    visible={form?.visible || {}}
                    started={form?.started || {}}
                    errors={form?.errors || {}}
                    onSubmit={handleSubmit}
                >
                    <FieldSet name="identity">
                        <div className="identification__fields">
                            <IdentificationMethod />
                            <div
                                className="identification__input-wrapper"
                                onBlur={(e: React.FocusEvent<HTMLDivElement>) => {
                                    if (
                                        selectedIdentificationMethod === VERIFICATION_TYPE.BANK_ID ||
                                        selectedIdentificationMethod === VERIFICATION_TYPE.KNOWN_BY_SALES_AGENT
                                    )
                                        return;
                                    const value = (e.target as HTMLInputElement).value.trim();
                                    if (value.length === 0) return;
                                    const validationName =
                                        IDENTIFICATION_VALIDATION_NAMES[
                                            selectedIdentificationMethod as IdentificationMethodType
                                        ];
                                    const error = validationName
                                        ? getErrorMessage(value, allValidationRules[validationName])
                                        : undefined;
                                    if (!error) {
                                        handleSubmit();
                                    }
                                }}
                            >
                                <IdentityInput
                                    identificationType={
                                        (selectedIdentificationMethod || "") as IdentificationMethodType
                                    }
                                    bankIdValue={bankIdContactValue}
                                    hideSubmitButton={shouldPollBankId && isBankIdAlreadySent}
                                    isLoading={isLoading}
                                    onSubmit={handleSubmit}
                                />
                                {isLoading && <Spinner size="sm" style={{ marginBottom: "8px" }} />}
                            </div>
                        </div>
                    </FieldSet>
                </RapidForm>
            )}
            {shouldPollBankId && (
                <div>
                    <ProgressBar isFetchingData={true} standByColor="light-grey" />
                    {!isUpdatingStatus && (
                        <Notification type="tile" variant="light-purple" mb="200">
                            <Notification.Icon name="info" />
                            <Notification.Text>
                                <Text>
                                    <strong>Status:</strong> {bankIdWaitingChannel} har blitt sendt. Venter på at kunde
                                    skal gjennomføre BankID...
                                </Text>
                            </Notification.Text>
                        </Notification>
                    )}
                    <div style={{ display: "flex", gap: "1rem" }}>
                        <Button variant="secondary" loading={isUpdatingStatus} onClick={handleUpdateStatus}>
                            Oppdater status
                        </Button>
                        <Button variant="tertiary-purple" onClick={() => dispatch(cancelBankIdPolling())}>
                            Avbryt
                        </Button>
                    </div>
                </div>
            )}
        </CustomerSection>
    );
};

export default Identification;

const verificationSuccessMessage = (method: IdentificationMethodType) => {
    switch (method) {
        case VERIFICATION_TYPE.BANK_ID:
            return "Identifisering med BankID er fullført";
        case VERIFICATION_TYPE.PASSPORT:
            return "Identifisering med pass er fullført";
        case VERIFICATION_TYPE.DRIVERS_LICENSE:
            return "Identifisering med førerkort er fullført";
        case VERIFICATION_TYPE.NATIONAL_ID_CARD:
            return "Identifisering med nasjonalt ID-kort er fullført";
        case VERIFICATION_TYPE.KNOWN_BY_SALES_AGENT:
            return "Kunden er identifisert som kjent av selger";
        default:
            // oxlint-disable-next-line no-console
            console.warn(`verificationSuccessMessage() - Unknown identification method: ${method}`);
            return "Identifisering er fullført";
    }
};

const IdentityInput = ({
    identificationType,
    bankIdValue,
    hideSubmitButton,
    isLoading,
    onSubmit,
}: {
    identificationType: IdentificationMethodType;
    bankIdValue: string;
    hideSubmitButton: boolean;
    isLoading: boolean;
    onSubmit: () => Promise<void>;
}) => {
    // Input doesn't forward refs, so focus/select the rendered <input> via the wrapper instead.
    const identificationInputWrapperRef = React.useRef<HTMLDivElement>(null);
    useEffect(() => {
        setTimeout(() => {
            const inputElement = identificationInputWrapperRef.current?.querySelector("input");
            inputElement?.focus();
            inputElement?.select();
        }, 0);
    }, [identificationType]);

    if (!identificationType) return null;

    const inputComponent = {
        PASSPORT: <Input type="text" name="passportNumber" label="Passport nummer" disabled={false} data-di-mask />,
        NATIONAL_ID_CARD: (
            <Input type="text" name="nationalId" label="Nasjonalt ID-nummer" disabled={false} data-di-mask />
        ),
        DRIVERS_LICENSE: (
            <Input type="text" name="driversLicense" label="Førerkortnummer" disabled={false} data-di-mask />
        ),
        BANK_ID: (
            <div className="identification__bank-id-row">
                <div className="identification__input" ref={identificationInputWrapperRef}>
                    <Input
                        type="text"
                        name="bankId"
                        label="Bank ID (tlf.nr eller e-post)"
                        placeholder="Skriv inn telefonnummer eller e-postadresse"
                        disabled={false}
                        data-di-mask
                    />
                </div>
                {bankIdValue.trim().length > 0 && !hideSubmitButton && (
                    <Button
                        className="identification__input-submit"
                        type="button"
                        variant="primary"
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={onSubmit}
                    >
                        Gjennomfør
                    </Button>
                )}
            </div>
        ),
        KNOWN_BY_SALES_AGENT: (
            <Button type="button" variant="primary" loading={isLoading} disabled={isLoading} onClick={onSubmit}>
                Bekreft at kunden er kjent av selger
            </Button>
        ),
    }[identificationType];

    return identificationType === "BANK_ID" || identificationType === "KNOWN_BY_SALES_AGENT" ? (
        inputComponent
    ) : (
        <div className="identification__input" ref={identificationInputWrapperRef}>
            {inputComponent}
        </div>
    );
};
